import React from "react";
import type { ApolloClient } from "@apollo/client";
import Alert from "components/Alert";
import { connect, type ConnectedProps } from "react-redux";
import { Formik } from "formik";
import { Col, Form, OverlayTrigger, Popover, Row } from "react-bootstrap";
import { toast } from "react-toastify";
import { get } from "lodash";
import Button from "components/Button";
import WithdrawalReasonSelect from "components/transferWidget/WithdrawalReasonSelect";
import { formatAmount, formatCurrency } from "utils/number";
import { createErrorSelector, createLoadingSelector } from "store/selectors";
import { disburseUserAccount, liquidateAccount } from "actions/adminActions";
import { adminConstants } from "actions/types";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { ScrollToFieldError } from "utils/form";
import { CloseAccountType } from "statics/closeAccountTypes";
import type { RootState } from "types/state";

import * as yup from "yup";

interface OwnProps {
  userId?: string;
  account?: {
    id?: string;
    assetBalance?: number;
  };
  onCancel?: React.MouseEventHandler<HTMLButtonElement>;
  onSuccess?: () => void;
}

/** Formik values; `amount` starts as null (a non-string) so it is typed wider
 *  than the schema's number — matching initialValues per the conversion rules. */
interface FormValues {
  isFullDisbursement: boolean;
  reason: string;
  amount: number | null;
  closeAccount: boolean;
}

interface State {
  showApprovalPopover: boolean;
}

const isUpdatingSelector = createLoadingSelector([
  adminConstants.DISPERSE_USER_ACCOUNT,
  adminConstants.LIQUIDATE_ACCOUNT,
]);

const errorSelector = createErrorSelector([
  adminConstants.DISPERSE_USER_ACCOUNT,
  adminConstants.LIQUIDATE_ACCOUNT,
]);

const mapStateToProps = (state: RootState) => {
  return {
    isUpdating: isUpdatingSelector(state),
    error: errorSelector(state),
  };
};

const mapDispatchToProps = {
  disburseUserAccount,
  liquidateAccount,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type Props = WithApolloClient<ConnectedProps<typeof connector> & OwnProps>;

class LiquidationForm extends React.PureComponent<Props, State> {
  _submitButton: HTMLButtonElement | null = null;

  constructor(props: Props) {
    super(props);

    this.state = {
      showApprovalPopover: false,
    };
  }

  _disburseAccount = (values: FormValues) => {
    if (values.isFullDisbursement) {
      if (values.closeAccount) {
        void this.props
          .liquidateAccount(this.props.client as ApolloClient<object>, {
            userId: this.props.userId!,
            accountId: this.props.account!.id!,
            closeAccount: values.closeAccount,
            reason: values.closeAccount ? values.reason : "",
            closeAccountType: CloseAccountType.SUPPORT_INITIATED,
          })
          .then((result) => {
            if (result.success) {
              toast.success("Successfully submitted account closure request.");
              document.body.click();
              this.props.onSuccess!();
            }
          });
      } else {
        void this.props
          .liquidateAccount(this.props.client as ApolloClient<object>, {
            userId: this.props.userId!,
            accountId: this.props.account!.id!,
            closeAccount: values.closeAccount,
            reason: values.closeAccount ? values.reason : "",
          })
          .then((result) => {
            if (result.success) {
              toast.success(
                "Successfully submitted full disbursement request."
              );
              document.body.click();
              this.props.onSuccess!();
            }
          });
      }
    } else {
      void this.props
        .disburseUserAccount(this.props.client as ApolloClient<object>, {
          userId: this.props.userId!,
          accountId: this.props.account!.id!,
          amount: values.amount!,
          reason: values.reason,
        })
        .then((result) => {
          if (result.success) {
            toast.success(
              "Successfully submitted partial disbursement request."
            );
            document.body.click();
            this.props.onSuccess!();
          }
        });
    }
  };

  render() {
    const accountBalance = get(this.props.account, "assetBalance", 0);

    const schema = yup.object({
      isFullDisbursement: yup
        .boolean()
        .label("Is Full Disbursement")
        .required(),
      reason: yup.string().when("isFullDisbursement", {
        is: false,
        then: (s) => s.required(),
        otherwise: (s) => s,
      }),
      amount: yup
        .number()
        .when("isFullDisbursement", {
          is: false,
          then: (s) =>
            s
              .required()
              .test(
                "amount",
                `Amount must be greater than 0`,
                (value) => value > 0 || accountBalance === 0
              ),
          otherwise: (s) => s.nullable(),
        })
        .test(
          "amount",
          `Amount must not be greater than account balance (${formatCurrency(
            accountBalance
          )})`,
          (value) => value! <= accountBalance
        ),
    });
    return (
      <div id={"account-liquidation"}>
        <div className="liquidation-form">
          <Formik<FormValues>
            validateOnChange={false}
            validationSchema={schema}
            onSubmit={() => this.setState({ showApprovalPopover: true })}
            enableReinitialize={true}
            initialValues={{
              isFullDisbursement: true,
              reason: "",
              amount: null,
              closeAccount: false,
            }}
          >
            {({
              handleSubmit,
              handleChange,
              values,
              touched,
              errors,
              handleBlur,
              setFieldValue,
            }) => (
              <Form noValidate onSubmit={handleSubmit}>
                <ScrollToFieldError />
                <Row className="mb-3">
                  <Form.Group
                    className="mb-3"
                    controlId="formIsFullDisbursement"
                  >
                    <Form.Label>Will this be a full disbursement?</Form.Label>

                    <Col md={6}>
                      <Form.Check
                        type="radio"
                        name="isFullDisbursement"
                        id="is-fulldisbursement"
                        label="Yes"
                        onChange={() =>
                          setFieldValue("isFullDisbursement", true)
                        }
                        checked={values.isFullDisbursement}
                      />
                    </Col>

                    <Col md={3}>
                      <Form.Check
                        type="radio"
                        name="isFullDisbursement"
                        label="No"
                        id="not-fulldisbursement"
                        onChange={() =>
                          setFieldValue("isFullDisbursement", false)
                        }
                        checked={!values.isFullDisbursement}
                      />
                    </Col>
                  </Form.Group>
                </Row>
                {values.isFullDisbursement && (
                  <Row className="mb-3">
                    <Form.Group className="mb-3" controlId="formCloseAccount">
                      <Col md={12}>
                        <Form.Check
                          type="checkbox"
                          name="closeAccount"
                          id="is-closeaccount"
                          label="Close Customer Account"
                          onChange={() => setFieldValue("closeAccount", true)}
                          checked={values.closeAccount}
                        />
                      </Col>
                    </Form.Group>
                  </Row>
                )}
                {(!values.isFullDisbursement || values.closeAccount) && (
                  <Row className="mb-3">
                    <Form.Group as={Col} sm={6} controlId="formBasicReason">
                      <Form.Label>Disbursement Reason</Form.Label>
                      <WithdrawalReasonSelect
                        value={values.reason}
                        handleChange={handleChange}
                        handleBlur={handleBlur}
                        touched={touched}
                        errors={errors}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.reason}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Row>
                )}
                {!values.isFullDisbursement && (
                  <Row className="mb-3">
                    <Form.Group as={Col} sm={6} controlId="formAdminRole">
                      <Form.Label>Disbursement Amount</Form.Label>
                      <Form.Control
                        sm={4}
                        type="number"
                        name="amount"
                        // @ts-expect-error -- value can be null here; Form.Control value expects string|number, null preserved from JS
                        value={values.amount}
                        onChange={(e) => {
                          const amount = e.target.value;
                          void setFieldValue("amount", formatAmount(amount));
                        }}
                        onBlur={handleBlur}
                        isInvalid={touched.amount && !!errors.amount}
                        isValid={touched.amount && !errors.amount}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.amount}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Row>
                )}
                <Form.Group as={Col}>
                  {this.props.error && (
                    <Alert type="error" msg={this.props.error} />
                  )}

                  <div className="submit-row">
                    <span className="cancel-btn">
                      <Button
                        btnLabel="Cancel"
                        color="cancel"
                        name="cancel"
                        type="button"
                        onClick={this.props.onCancel}
                      />
                    </span>
                    <OverlayTrigger
                      rootClose
                      trigger={[]}
                      // @ts-expect-error -- OverlayTrigger types `target` as `never`; inert prop preserved from JS
                      target={this._submitButton}
                      placement="bottom"
                      show={this.state.showApprovalPopover}
                      onToggle={(showApprovalPopover) =>
                        this.setState({ showApprovalPopover })
                      }
                      onExit={() =>
                        this.setState({ showApprovalPopover: false })
                      }
                      overlay={
                        <Popover className="action-popover">
                          <Popover.Body>
                            <p>
                              Are you sure you want to create a disbursement
                              request?
                            </p>
                            <div>
                              <Button
                                type="button"
                                color="cancel"
                                name="cancel"
                                btnLabel="Cancel"
                                onClick={() => document.body.click()}
                                size="sm"
                              />
                              <Button
                                type="button"
                                color="red"
                                name="submit"
                                btnLabel="Disburse"
                                withArrow={true}
                                loading={this.props.isUpdating}
                                onClick={() => this._disburseAccount(values)}
                                size="sm"
                              />
                            </div>
                          </Popover.Body>
                        </Popover>
                      }
                    >
                      <Button
                        color="action"
                        name="action"
                        btnLabel={"Disburse"}
                        size={"sm"}
                        // @ts-expect-error -- React 19 ref callbacks may not return a value; implicit-return assignment preserved from JS
                        ref={(ref) => (this._submitButton = ref)}
                      />
                    </OverlayTrigger>
                  </div>
                </Form.Group>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    );
  }
}

export default connector(withApollo<Props>(LiquidationForm));
